import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { getApiErrorUserMessage } from "_shared/_errors/api-error";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import MessageNotification from "notifications/message-notifications/message-notification";
import MessageNotificationSchema, {
  MessageNotificationJSON,
} from "notifications/message-notifications/_schemas/message-notification-schema";
import { notificationsApi } from "notifications/_api/notifications-api";
import NotificationQuery from "notifications/_api/queries/notification-query";
import NotificationQuerySchema, {
  NotificationQueryJSON,
} from "notifications/_api/queries/_schemas/notification-query-schema";
import Notification from "notifications/notification";
import { readNotificationApiError } from "notifications/notification-error";
import NotificationSchema, { NotificationJSON } from "notifications/_schemas/notification-schema";
import { NotificationType } from "notifications/notification-type";
import Session from "users/__session/session";

export type NotificationLoadIssue = { id: Guid; issue: string };
type AnyNotificationJSON = NotificationJSON | MessageNotificationJSON;
type RecipientJSON = { userId: string; seenAt?: string | null };

/**
 * Flips one recipient's `seenAt` directly on the wire-shaped JSON, without deserializing into a
 * domain model first. `NotificationJSON` and `MessageNotificationJSON` share the same `recipients`
 * shape, so this needs no branch on which one it is, and it costs nothing to call from a reducer
 * that must stay synchronous (GitHub issue #4950).
 */
const withRecipientSeenAt = (json: AnyNotificationJSON, userId: Guid, seenAt: string | null): AnyNotificationJSON => {
  const recipients = (json as { recipients?: RecipientJSON[] }).recipients;
  if (!recipients) return json;

  return {
    ...json,
    recipients: recipients.map((recipient) => (recipient.userId === userId.value ? { ...recipient, seenAt } : recipient)),
  } as AnyNotificationJSON;
};

export const isNotificationLoadIssue = (value: object): value is NotificationLoadIssue => {
  return "issue" in value;
};
const isMessageNotificationJSON = (value: AnyNotificationJSON): value is MessageNotificationJSON => {
  // MessageNotificationJSON extends NotificationJSON with an optional messageInfo field
  return "messageInfo" in value;
};

export type NotificationStoreState = {
  byId: {
    entries: Record<string, AnyNotificationJSON>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, PaginatedResponse<AnyNotificationJSON | NotificationLoadIssue>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, NotificationQueryJSON>;
    lastSuccessfulResponses: Record<string, PaginatedResponse<AnyNotificationJSON | NotificationLoadIssue>>;
  };
};

const initialState: NotificationStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
    lastSuccessfulResponses: {},
  },
};

export const populateNotificationsByQuery = createAsyncThunk<
  {
    paginatedResponse: PaginatedResponse<NotificationJSON | MessageNotificationJSON | { id: Guid; issue: string }>;
    issueCount: number;
  },
  { session: Session; query: NotificationQuery; interfaceName: string },
  { rejectValue: string }
>(
  "notifications/populateNotificationsByQuery",
  async ({ query }: { session: Session; query: NotificationQuery }, thunkAPI) => {
    const request = thunkAPI.dispatch(
      notificationsApi.endpoints.getNotifications.initiate({ query }, { forceRefetch: true }),
    );
    const onAbort = () => request.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const responseJson = await request.unwrap();
      const validatedData: (NotificationJSON | MessageNotificationJSON | NotificationLoadIssue)[] = [];

      let issueCount = 0;
      responseJson.data.forEach((notification: NotificationJSON | MessageNotificationJSON) => {
        let id = Guid.EMPTY;
        try {
          id = Guid.fromJSON(notification.id) ?? id;
          if (
            ("messageInfo" in notification &&
              notification.messageInfo?.id &&
              MessageNotificationSchema.validateJSON(notification)) ||
            NotificationSchema.validateJSON(notification)
          ) {
            validatedData.push(notification);
          }
        } catch (error) {
          validatedData.push({ id, issue: "Error loading notification." });
          console.error("Error validating notification JSON:", error);
          issueCount++;
        }
      });
      const paginatedResponse = PaginatedResponse.from<
        NotificationJSON | MessageNotificationJSON | NotificationLoadIssue
      >(validatedData, responseJson.headers);
      return { paginatedResponse, issueCount };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        getApiErrorUserMessage(readNotificationApiError(error)) ?? "Unable to load notifications.",
      );
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
      request.unsubscribe();
    }
  },
  {
    condition: ({ query }: { session: Session; query: NotificationQuery }, { getState }) => {
      const state = getState() as RootState;
      const queryKey = query.cacheKey;
      // Only dispatch if not already loading
      return !state.notifications.byQuery.loading[queryKey];
    },
  }
);
export const populateNotificationById = createAsyncThunk<Notification, { session: Session; id: Guid }, { rejectValue: string }>(
  "notifications/populateNotificationById",
  async ({ id }: { session: Session; id: Guid }, thunkAPI) => {
    const request = thunkAPI.dispatch(
      notificationsApi.endpoints.getNotificationById.initiate({ notificationId: id.value }, { forceRefetch: true }),
    );
    const onAbort = () => request.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);
    try {
      return await request.unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        getApiErrorUserMessage(readNotificationApiError(error)) ?? "Unable to load notification.",
      );
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
      request.unsubscribe();
    }
  },
  {
    condition: ({ id }: { session: Session; id: Guid }, { getState }) => {
      const state = getState() as RootState;
      const idKey = id.value;
      // Only dispatch if not already loading
      return !state.notifications.byId.loading[idKey];
    },
  }
);
const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      const id: Guid = action.payload.id;
      if (!id) return;

      const json = action.payload.toJSON();
      if (json !== undefined && json !== null) {
        state.byId.entries[id.value] = json;
      }

      // Invalidate query cache
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    /**
     * Applies a whole bulk change in one reducer pass (GitHub issue #2963).
     *
     * `addNotification` clears the query cache on every call, so folding a batch by dispatching it
     * once per notification produced one store notification — and one render of everything reading
     * notifications — per row the user had just changed. This writes every entry and clears the
     * query cache once, so a bulk mark-seen costs a single render.
     */
    addNotifications: (state, action: PayloadAction<Notification[]>) => {
      let anyStored = false;

      for (const notification of action.payload) {
        const id: Guid = notification.id;
        if (!id) continue;

        const json = notification.toJSON();
        if (json !== undefined && json !== null) {
          state.byId.entries[id.value] = json;
          anyStored = true;
        }
      }

      if (!anyStored) return;

      // Invalidate query cache
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    removeNotification: (state, action: PayloadAction<Guid>) => {
      const id = action.payload.value;
      delete state.byId.entries[id];

      // Invalidate query cache
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    updateNotificationInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: NotificationQuery;
      }>
    ) => {
      const { interfaceName, query } = action.payload;

      state.interfaceQueries.entries[interfaceName] = NotificationQuerySchema.serialize(query);
    },
    markAllNotificationsSeenForUser: (state, action: PayloadAction<Guid | undefined>) => {
      const userId = action.payload;
      if (!userId) {
        console.error("Failed to mark all notifications as seen. User ID is undefined.");
        return;
      }

      for (const [query, notificationPage] of Object.entries(state.byQuery.entries)) {
        const jsonPage = notificationPage as PaginatedResponse<AnyNotificationJSON>;

        const newData: AnyNotificationJSON[] = jsonPage.data
          .map((notificationJson: AnyNotificationJSON) => {
            if (isNotificationLoadIssue(notificationJson)) {
              return notificationJson;
            }
            const model = isMessageNotificationJSON(notificationJson)
              ? MessageNotification.fromJSON(notificationJson)
              : Notification.fromJSON(notificationJson);

            model?.markSeenForUser(userId);
            return model?.toJSON();
          })
          .filter((json): json is AnyNotificationJSON => json !== undefined && json !== null);

        state.byQuery.entries[query] = jsonPage.appendOrReplaceData(newData);
      }
    },
    /**
     * Optimistically flips one notification's seen state for one user, everywhere it is cached
     * (GitHub issue #4950). Unlike `addNotification`, this leaves every other cached query page
     * alone and clears nothing, so the bell's per-row "Mark as read" control can dispatch it the
     * instant it is clicked — before the API call resolves — without forcing a refetch of
     * everything the bell has loaded.
     */
    markNotificationSeenForUser: (state, action: PayloadAction<{ userId: Guid | undefined; notificationId: Guid }>) => {
      const { userId, notificationId } = action.payload;
      if (!userId) {
        console.error("Failed to mark notification as seen. User ID is undefined.");
        return;
      }

      for (const [query, notificationPage] of Object.entries(state.byQuery.entries)) {
        const jsonPage = notificationPage as PaginatedResponse<AnyNotificationJSON>;
        let changed = false;
        const newData: AnyNotificationJSON[] = jsonPage.data.map((notificationJson: AnyNotificationJSON) => {
          if (isNotificationLoadIssue(notificationJson) || notificationJson.id !== notificationId.value) {
            return notificationJson;
          }
          changed = true;
          return withRecipientSeenAt(notificationJson, userId, new Date().toISOString());
        });
        if (changed) {
          state.byQuery.entries[query] = jsonPage.appendOrReplaceData(newData);
        }
      }

      const existingById = state.byId.entries[notificationId.value];
      if (existingById) {
        state.byId.entries[notificationId.value] = withRecipientSeenAt(existingById, userId, new Date().toISOString());
      }
    },
    /**
     * Reverts `markNotificationSeenForUser` when the API call it was standing in for fails
     * (GitHub issue #4950). This puts the row back the way it was rather than asking the server
     * what it thinks, since the point is to undo the local guess, not to resync.
     */
    markNotificationUnseenForUser: (state, action: PayloadAction<{ userId: Guid | undefined; notificationId: Guid }>) => {
      const { userId, notificationId } = action.payload;
      if (!userId) {
        console.error("Failed to mark notification as unseen. User ID is undefined.");
        return;
      }

      for (const [query, notificationPage] of Object.entries(state.byQuery.entries)) {
        const jsonPage = notificationPage as PaginatedResponse<AnyNotificationJSON>;
        let changed = false;
        const newData: AnyNotificationJSON[] = jsonPage.data.map((notificationJson: AnyNotificationJSON) => {
          if (isNotificationLoadIssue(notificationJson) || notificationJson.id !== notificationId.value) {
            return notificationJson;
          }
          changed = true;
          return withRecipientSeenAt(notificationJson, userId, null);
        });
        if (changed) {
          state.byQuery.entries[query] = jsonPage.appendOrReplaceData(newData);
        }
      }

      const existingById = state.byId.entries[notificationId.value];
      if (existingById) {
        state.byId.entries[notificationId.value] = withRecipientSeenAt(existingById, userId, null);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(populateNotificationsByQuery.pending, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) return;
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.loading[query] = true;
      state.byQuery.error[query] = null;
    });
    builder.addCase(populateNotificationsByQuery.fulfilled, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) return;

      const queryKey = action.meta.arg.query.cacheKey;
      const interfaceName = action.meta.arg.interfaceName;
      state.byQuery.loading[queryKey] = false;

      const { paginatedResponse, issueCount } = action.payload;
      state.byQuery.entries[queryKey] = paginatedResponse;
      if (interfaceName) {
        // Update last successful response for this interface
        state.interfaceQueries.lastSuccessfulResponses[interfaceName] = paginatedResponse;
      }
      if (issueCount > 0) {
        state.byQuery.error[queryKey] = `${issueCount} notification(s) could not be loaded due to errors.`;
      }
    });
    builder.addCase(populateNotificationsByQuery.rejected, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) return;
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.loading[query] = false;
      state.byQuery.error[query] = action.payload as string;
    });

    builder.addCase(populateNotificationById.pending, (state, action) => {
      if (!action.meta.arg.id) return;
      const id = action.meta.arg.id.value;
      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });
    builder.addCase(populateNotificationById.fulfilled, (state, action) => {
      if (!action.meta.arg.id) return;
      const id = action.meta.arg.id.value;

      state.byId.loading[id] = false;

      const notification = action.payload;
      const json = notification.toJSON();
      if (json !== undefined && json !== null) {
        state.byId.entries[id] = json;
      }
    });
    builder.addCase(populateNotificationById.rejected, (state, action) => {
      if (!action.meta.arg.id) {
        return;
      }
      const id = action.meta.arg.id.value;
      state.byId.loading[id] = false;
      state.byId.error[id] = action.payload as string;
    });
  },
});

export const {
  addNotification,
  addNotifications,
  markAllNotificationsSeenForUser,
  markNotificationSeenForUser,
  markNotificationUnseenForUser,
  removeNotification,
  updateNotificationInterfaceQuery,
} = notificationsSlice.actions;

const selectNotificationsEntriesForQuery = (state: RootState) => state.notifications.byQuery.entries;
export const selectNotificationsByQueryKey = createSelector(
  [selectNotificationsEntriesForQuery, (_: RootState, queryKey: string) => queryKey],
  (
    entries: Record<string, PaginatedResponse<AnyNotificationJSON | NotificationLoadIssue>>,
    queryKey: string
  ): PaginatedResponse<NotificationType | NotificationLoadIssue> | undefined => {
    const pagedJson = entries[queryKey];
    if (!pagedJson) return;

    const deserializedData: (NotificationType | NotificationLoadIssue)[] = [];
    pagedJson.data.forEach((notification: AnyNotificationJSON | NotificationLoadIssue) => {
      try {
        if (isNotificationLoadIssue(notification)) {
          deserializedData.push(notification);
        } else if (isMessageNotificationJSON(notification)) {
          deserializedData.push(MessageNotification.fromJSON(notification));
        } else {
          deserializedData.push(Notification.fromJSON(notification) as NotificationType);
        }
      } catch (error) {
        console.error("Error deserializing notification:", error);
      }
    });

    return PaginatedResponse.from<NotificationType | NotificationLoadIssue>(deserializedData, pagedJson.headers);
  }
);
export const getNotificationsByQuery = (query: NotificationQuery) => (state: RootState) =>
  selectNotificationsByQueryKey(state, query.cacheKey);

export const getIsLoadingNotificationsByQuery = (query?: NotificationQuery) => (state: RootState) => {
  if (query === undefined) return false;
  return state.notifications.byQuery.loading[query.cacheKey] ?? false;
};
export const getErrorLoadingNotificationsByQuery = (query?: NotificationQuery) => (state: RootState) => {
  if (!query) return;
  return state.notifications.byQuery.error[query.cacheKey];
};

export const getNotificationById = (id: Guid) => {
  return (state: RootState) => {
    if (!id) return;
    const notificationJson = state.notifications.byId.entries[id.value];
    return isMessageNotificationJSON(notificationJson)
      ? MessageNotification.fromJSON(notificationJson)
      : Notification.fromJSON(notificationJson);
  };
};
export const getIsLoadingNotificationById = (id: Guid) => (state: RootState) =>
  state.notifications.byId.loading[id.value];
export const getErrorLoadingNotificationById = (id: Guid) => (state: RootState) =>
  state.notifications.byId.error[id.value];

const selectNotificationQueryByInterface = createSelector(
  [(_: RootState, interfaceName: string) => interfaceName, (state: RootState) => state.notifications.interfaceQueries],
  (interfaceName: string, interfaceQueries): NotificationQuery | undefined => {
    const json = interfaceQueries.entries[interfaceName];
    if (!json) return;
    return NotificationQuerySchema.deserialize(json);
  }
);
export const getNotificationQueryByInterface = (interfaceName: string) => (state: RootState) => {
  return selectNotificationQueryByInterface(state, interfaceName);
};

const selectLastSuccessfulResponsesEntries = (state: RootState) =>
  state.notifications.interfaceQueries.lastSuccessfulResponses;
const selectLastSuccessfulNotificationsResponseByInterface = createSelector(
  [selectLastSuccessfulResponsesEntries, (_: RootState, interfaceName: string) => interfaceName],
  (
    entries: Record<string, PaginatedResponse<AnyNotificationJSON | NotificationLoadIssue>>,
    interfaceName: string
  ): PaginatedResponse<NotificationType | NotificationLoadIssue> | undefined => {
    const pagedJson = entries[interfaceName];
    if (!pagedJson) return;

    const notificationData = pagedJson.data
      .map((notificationJson: AnyNotificationJSON | NotificationLoadIssue | undefined) => {
        if (!notificationJson) return undefined;
        if (isNotificationLoadIssue(notificationJson)) {
          return notificationJson;
        }
        return isMessageNotificationJSON(notificationJson)
          ? MessageNotification.fromJSON(notificationJson)
          : Notification.fromJSON(notificationJson);
      })
      .filter((n): n is NotificationType | NotificationLoadIssue => typeof n === "object" && n !== null);
    return PaginatedResponse.from<NotificationType | NotificationLoadIssue>(notificationData, pagedJson.headers);
  }
);
export const getLastSuccessfulNotificationResponseByInterface = (interfaceName: string) => (state: RootState) => {
  return selectLastSuccessfulNotificationsResponseByInterface(state, interfaceName);
};

export default notificationsSlice;
